import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale, // Add this import
  TimeScale,
  Tooltip
} from 'chart.js';

// Register the required Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale, // Register the CategoryScale
  TimeScale,
  Tooltip
);

const apiUrl = process.env.REACT_APP_API_URL;

const RelatedCoins = ({ coinId, homeUrl }) => {
  const navigate = useNavigate();
  const [relatedCoins, setRelatedCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRefs = useRef({});

  useEffect(() => {
    const fetchRelatedCoins = async () => {
      try {
        // First get the current coin's data
        const coinResponse = await fetch(`${apiUrl}/get-coindetails-data?coinId=${coinId}`);
        const coinData = await coinResponse.json();
        
        // Get the first category or default to empty string
        const category = coinData.categories?.[0]?.replace(/\s+/g, '-').toLowerCase() || '';
        
        // Build API URL - try with category first
        let aurl = `${apiUrl}/get-relatedcoin-data`;
        
        if (category) {
          aurl += `&category=${category}`;
        }

        const response = await fetch(aurl);
        
        if (!response.ok) {
          // If category fails, try without category
          const fallbackResponse = await fetch(`${apiUrl}/get-relatedcoin-data`);
          
          if (!fallbackResponse.ok) {
            throw new Error('Failed to fetch related coins');
          }
          
          const data = await fallbackResponse.json();
          setRelatedCoins(data.filter(coin => coin.id !== coinId).slice(0, 10));
        } else {
          const data = await response.json();
          setRelatedCoins(data.filter(coin => coin.id !== coinId).slice(0, 10));
        }
      } catch (err) {
        console.error("Error fetching related coins:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedCoins();

    // Cleanup function to destroy charts when component unmounts
    return () => {
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, [coinId]);

  // Initialize sparkline charts when relatedCoins updates
  useEffect(() => {
    if (relatedCoins.length > 0) {
      relatedCoins.forEach((coin, index) => {
        const canvasId = `sparkline-${coin.id}-${index}`;
        const canvasElement = document.getElementById(canvasId);
        
        if (canvasElement && coin.sparkline_in_7d?.price && !chartRefs.current[canvasId]) {
          const ctx = canvasElement.getContext('2d');
          
          chartRefs.current[canvasId] = new Chart(ctx, {
            type: 'line',
            data: {
              labels: coin.sparkline_in_7d.price.map((_, i) => i),
              datasets: [{
                data: coin.sparkline_in_7d.price,
                borderColor: coin.price_change_percentage_24h >= 0 ? '#00C853' : '#FF3D00',
                borderWidth: 1,
                fill: false,
                tension: 0.4,
                pointRadius: 0
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: { enabled: false }
              },
              scales: {
                x: { 
                  display: false,
                  type: 'category' // Explicitly use category scale
                },
                y: { 
                  display: false,
                  type: 'linear' // Explicitly use linear scale
                }
              }
            }
          });
        }
      });
    }
  }, [relatedCoins]);

  if (loading) return <div className="loading">Loading related coins...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!relatedCoins.length) return null;

  return (
    <div className="relatedcoin_mainsec">
      <div className="titlebox">
        <h2>Related Coins You May Like</h2>
      </div>
      
      <div className="relatedcoin_boxsec">
        {relatedCoins.slice(0, 8).map((coin, index) => (
          <div className="relatedcoin_box" key={`${coin.id}-${index}`}>
            <div className="imgbox">
              <img 
                alt={coin.name} 
                src={coin.image || `${homeUrl}/assets/img/default-coin.png`} 
                onError={(e) => {
                  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
                }}
              />
              
              <div className="starbox">
                <i className="far fa-star"></i>
              </div>
            </div>
            
            <div className="namebox">
              
              <a href={`/coin/${coin.id}`} onClick={() => navigate(`/coin/${coin.id}`)}><h4>{coin.name}</h4></a>
            </div>
            
            <div className="pricupdown">
              <h2>
                ${coin.current_price?.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6
                }) || 'N/A'}
                <span className={coin.price_change_percentage_24h >= 0 ? "upcoin" : "downcoin"}>
                  <i className={`fas fa-sort-${coin.price_change_percentage_24h >= 0 ? "up" : "down"}`}></i>
                  {Math.abs(coin.price_change_percentage_24h || 0).toFixed(2)}%
                </span>
              </h2>
            </div>
            
            <div className="sparkline_img">
              {coin.sparkline_in_7d?.price ? (
                <canvas 
                  id={`sparkline-${coin.id}-${index}`}
                  style={{ width: '100%', height: '80px' }}
                />
              ) : (
                <img 
                  alt="sparkline" 
                  src={`${homeUrl}/assets/img/sparkline.svg`} 
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RelatedCoins;